import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import CreateDeviceRequest from "./CreateDeviceRequest";
import VenueFacts from "./VenueFacts";
import DeviceMap from "./DeviceMap";
import { API_BASE_URL } from "../../config";
import "../../styles/Browse.css";

export default function DeviceSummaryDetailsPage() {
    const { deviceId } = useParams();
    const [searchParams] = useSearchParams();
    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const isCustomer = localStorage.getItem("role") === "customer";
    const isLoggedIn = !!localStorage.getItem("token");
    const showRequest = isCustomer && searchParams.get("request") === "1";
    // `device` is null until the fetch below returns, so reading device.vendor_id
    // at render time threw on the very first paint for any logged-in vendor --
    // a white screen, not a missing field. JSON.parse also throws on a stored
    // value that is not JSON, which takes the page down the same way.
    let user = null;
    try {
        user = JSON.parse(localStorage.getItem("user"));
    } catch {
        user = null;
    }
    const isVendor = !!user && user.role === "vendor";
    const isOwnDevice = isVendor && !!device && device.vendor_id === user.vendor_id;

    useEffect(() => {
        async function fetchDevice() {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/devices/summary/${deviceId}`);
                const data = await res.json();
                setDevice(data.device || null);
            } catch {
                setDevice(null);
            }
            setLoading(false);
        }
        fetchDevice();
    }, [deviceId]);

    const handleRequestClick = () => {
        if (isLoggedIn && isCustomer) {
            navigate(`/devices/${deviceId}?request=1`);
        } else {
            navigate("/register/customer");
        }
    };

    if (loading) return <div className="dv"><p className="wb-sub">Loading screen…</p></div>;

    if (!device) {
        return (
            <div className="dv">
                <h1 className="dv__title">Screen not found</h1>
                <p className="wb-sub">
                    This listing may have been removed, or the link may be wrong.
                </p>
                <Link to="/devices" className="wb-btn wb-btn--primary">Browse screens</Link>
            </div>
        );
    }

    const rate = device.daily_rate != null ? Number(device.daily_rate) : null;
    const canBook = isCustomer || (isVendor && !isOwnDevice);

    return (
        <div className="dv">
            <Link to="/devices" className="wb-btn wb-btn--ghost wb-btn--sm dv__back">
                ← All screens
            </Link>

            <h1 className="dv__title">{device.name || "Unnamed screen"}</h1>
            <p className="dv__addr">{device.location || "Location not given"}</p>

            {/* The same component, from the same payload, as the browse card.
                The layout differs because the page does; the CONTENT cannot,
                which is the point -- a screen that promised 500 visitors a week
                in the listing must not say something else on the page the
                listing links to. */}
            <VenueFacts device={device} className="dv__venue" />

            <dl className="dv__facts">
                {/* The code printed on the screen itself while it is unbooked.
                    First, because it is how someone who saw the screen in a
                    window confirms this is the same one. */}
                {device.public_code && (
                    <div>
                        <dt>Screen code</dt>
                        <dd><code className="device-code">{device.public_code}</code></dd>
                    </div>
                )}
                <div>
                    <dt>Day rate</dt>
                    <dd>
                        {rate != null ? (
                            <span className="dv__price">${rate.toFixed(2)}</span>
                        ) : (
                            <span className="wb-badge wb-badge--warn">Not set</span>
                        )}
                    </dd>
                </div>
                <div>
                    <dt>Per hour</dt>
                    <dd>
                        {device.hourly_rate_label
                            ? <span className="dv__price">{device.hourly_rate_label}</span>
                            : <span className="wb-badge wb-badge--warn">Not set</span>}
                    </dd>
                </div>
                {/* WAS `device.status`, which is the stale column: the only
                    thing that writes it is a Celery task no deployment runs, so
                    this page reported "active" for screens that had been dark
                    for weeks -- while the browse card the visitor clicked
                    through FROM said "Not checking in", correctly, from
                    listing_state. Two pages, two answers, and the one somebody
                    reads before paying was the wrong one. */}
                <div>
                    <dt>Right now</dt>
                    <dd>
                        {device.bookable
                            ? <span className="wb-badge wb-badge--ok">Online and bookable</span>
                            : <span className="wb-badge wb-badge--warn">Not bookable</span>}
                    </dd>
                </div>
                {device.on_air && (
                    <div>
                        <dt>On air</dt>
                        {/* The screen's own local time, not the reader's: an
                            advertiser buys the salon's 9am. */}
                        <dd>{device.on_air}{device.timezone ? ` (${device.timezone})` : ""}</dd>
                    </div>
                )}
                {device.orientation && (
                    <div>
                        <dt>Screen</dt>
                        <dd>
                            {device.orientation === "portrait"
                                ? "Portrait — a tall ad fills it best"
                                : "Landscape — a wide ad fills it best"}
                        </dd>
                    </div>
                )}
                {/* The endpoint also returns vendor_username, but users no longer
                    have a username column so it is always null. Rendering it
                    produced an empty "Vendor:" line on every listing. */}
                {device.vendor_business_name && (
                    <div>
                        <dt>Listed by</dt>
                        <dd>{device.vendor_business_name}</dd>
                    </div>
                )}
            </dl>

            {device.unavailable_reason && (
                <div className="wb-note wb-note--warn">
                    {device.unavailable_reason}
                </div>
            )}

            {/* Roughly where it is. The browse page has had a map since it was
                built; this page -- the one somebody reads before paying -- had
                only the address as text. Rendered only when the row has been
                geocoded, because a map centred on null is a map of the
                Atlantic. */}
            {device.latitude != null && device.longitude != null && (
                <div className="dv__map">
                    {/* `focus`: centre on this screen at street zoom, with no
                        geolocation prompt. Without it the map asks where the
                        VIEWER is and centres there, leaving this screen's pin
                        off the edge -- right for browse, wrong here.

                        `height` as a prop, not CSS: MapContainer writes the
                        height inline on .leaflet-container, so a stylesheet
                        rule loses to it. */}
                    <DeviceMap devices={[device]} focus height={280} />
                </div>
            )}

            {/* handleRequestClick has always had a branch sending a signed-out
                visitor to registration, but nothing could reach it: the button
                only rendered for a customer or a non-owner vendor, and an
                anonymous visitor is neither. So the one page where someone
                decides to sign up had no call to action on it at all. */}
            {!isLoggedIn && rate != null && (
                <button
                    type="button"
                    className="wb-btn wb-btn--primary wb-btn--lg"
                    onClick={handleRequestClick}
                >
                    Create an account to book
                </button>
            )}

            {isLoggedIn && canBook && !showRequest && rate != null && (
                <button
                    type="button"
                    className="wb-btn wb-btn--primary wb-btn--lg"
                    onClick={handleRequestClick}
                >
                    Book this screen
                </button>
            )}

            {showRequest && canBook && (
                <CreateDeviceRequest deviceId={deviceId} device={device} />
            )}
        </div>
    );
}
